/**
 * The menu tab a customer last had open, per restaurant.
 *
 * The menu screen unmounts on the way to checkout, so a tab held in component
 * state came back as the first one: tapping *Review order* and then back lost
 * your place in a menu you were halfway down.
 *
 * Per restaurant rather than global, because menus are unrelated — the tab left
 * open at one place says nothing about the tab wanted at another. Kept in
 * `localStorage` like the cart, so a reload does not lose it either. A tab that
 * no longer exists is ignored by the caller, which falls back to the first.
 */

import { useCallback } from 'react';

import { usePersistentState } from '@yummy/ui-rnr';

const STORAGE_KEY = 'yummy.customer.menuCategory.v1';

/**
 * Reads and remembers the open tab.
 *
 * @param restaurantId The restaurant whose menu is on screen, when known.
 * @returns The remembered tab, and a way to remember another.
 */
export function useMenuCategory(restaurantId: string | null): {
  /** The tab this restaurant was left on, or null if it has not been visited. */
  category: string | null;
  remember: (categoryId: string) => void;
} {
  const [byRestaurant, setByRestaurant] = usePersistentState<Record<string, string>>(
    STORAGE_KEY,
    {}
  );

  const remember = useCallback(
    (categoryId: string) => {
      if (!restaurantId) return;
      setByRestaurant({ ...byRestaurant, [restaurantId]: categoryId });
    },
    [restaurantId, byRestaurant, setByRestaurant]
  );

  return {
    category: restaurantId ? (byRestaurant[restaurantId] ?? null) : null,
    remember,
  };
}
